/** Таблицы перевода единиц (к базовой SI / особые для температуры). */

export type UnitDef = {
  id: string;
  label: string;
  /** Множитель к базовой единице категории (кроме температуры). */
  toBase: number;
};

export type UnitCategory = {
  id: string;
  label: string;
  kind: "linear" | "temperature";
  units: UnitDef[];
};

export const UNIT_CATEGORIES: UnitCategory[] = [
  {
    id: "length",
    label: "Длина",
    kind: "linear",
    units: [
      { id: "m", label: "м", toBase: 1 },
      { id: "km", label: "км", toBase: 1000 },
      { id: "cm", label: "см", toBase: 0.01 },
      { id: "mm", label: "мм", toBase: 0.001 },
      { id: "mi", label: "мили", toBase: 1609.344 },
      { id: "yd", label: "ярды", toBase: 0.9144 },
      { id: "ft", label: "футы", toBase: 0.3048 },
      { id: "in", label: "дюймы", toBase: 0.0254 },
    ],
  },
  {
    id: "mass",
    label: "Вес и масса",
    kind: "linear",
    units: [
      { id: "kg", label: "кг", toBase: 1 },
      { id: "g", label: "г", toBase: 0.001 },
      { id: "mg", label: "мг", toBase: 1e-6 },
      { id: "t", label: "т", toBase: 1000 },
      { id: "lb", label: "фунты", toBase: 0.45359237 },
      { id: "oz", label: "унции", toBase: 0.028349523125 },
    ],
  },
  {
    id: "temperature",
    label: "Температура",
    kind: "temperature",
    units: [
      { id: "c", label: "°C", toBase: 1 },
      { id: "f", label: "°F", toBase: 1 },
      { id: "k", label: "K", toBase: 1 },
    ],
  },
  {
    id: "energy",
    label: "Энергия",
    kind: "linear",
    units: [
      { id: "j", label: "Дж", toBase: 1 },
      { id: "kj", label: "кДж", toBase: 1000 },
      { id: "cal", label: "кал", toBase: 4.184 },
      { id: "kcal", label: "ккал", toBase: 4184 },
      { id: "wh", label: "Вт·ч", toBase: 3600 },
      { id: "kwh", label: "кВт·ч", toBase: 3.6e6 },
    ],
  },
  {
    id: "area",
    label: "Площадь",
    kind: "linear",
    units: [
      { id: "m2", label: "м²", toBase: 1 },
      { id: "km2", label: "км²", toBase: 1e6 },
      { id: "cm2", label: "см²", toBase: 1e-4 },
      { id: "ha", label: "га", toBase: 10000 },
      { id: "acre", label: "акры", toBase: 4046.8564224 },
      { id: "ft2", label: "футы²", toBase: 0.09290304 },
    ],
  },
  {
    id: "speed",
    label: "Скорость",
    kind: "linear",
    units: [
      { id: "mps", label: "м/с", toBase: 1 },
      { id: "kmh", label: "км/ч", toBase: 1 / 3.6 },
      { id: "mph", label: "миль/ч", toBase: 0.44704 },
      { id: "knot", label: "узлы", toBase: 0.514444 },
    ],
  },
  {
    id: "time",
    label: "Время",
    kind: "linear",
    units: [
      { id: "s", label: "с", toBase: 1 },
      { id: "min", label: "мин", toBase: 60 },
      { id: "h", label: "ч", toBase: 3600 },
      { id: "d", label: "сут", toBase: 86400 },
      { id: "ms", label: "мс", toBase: 0.001 },
    ],
  },
  {
    id: "power",
    label: "Мощность",
    kind: "linear",
    units: [
      { id: "w", label: "Вт", toBase: 1 },
      { id: "kw", label: "кВт", toBase: 1000 },
      { id: "hp", label: "л.с.", toBase: 735.49875 },
    ],
  },
  {
    id: "data",
    label: "Данные",
    kind: "linear",
    units: [
      { id: "b", label: "Б", toBase: 1 },
      { id: "kb", label: "КБ", toBase: 1024 },
      { id: "mb", label: "МБ", toBase: 1024 ** 2 },
      { id: "gb", label: "ГБ", toBase: 1024 ** 3 },
      { id: "tb", label: "ТБ", toBase: 1024 ** 4 },
      { id: "bit", label: "бит", toBase: 1 / 8 },
    ],
  },
  {
    id: "pressure",
    label: "Давление",
    kind: "linear",
    units: [
      { id: "pa", label: "Па", toBase: 1 },
      { id: "kpa", label: "кПа", toBase: 1000 },
      { id: "bar", label: "бар", toBase: 1e5 },
      { id: "atm", label: "атм", toBase: 101325 },
      { id: "mmhg", label: "мм рт.ст.", toBase: 133.322 },
      { id: "psi", label: "psi", toBase: 6894.757 },
    ],
  },
  {
    id: "angle",
    label: "Угол",
    kind: "linear",
    units: [
      { id: "deg", label: "°", toBase: 1 },
      { id: "rad", label: "рад", toBase: 180 / Math.PI },
      { id: "grad", label: "град", toBase: 0.9 },
    ],
  },
  {
    id: "volume",
    label: "Объём",
    kind: "linear",
    units: [
      { id: "m3", label: "м³", toBase: 1 },
      { id: "l", label: "л", toBase: 0.001 },
      { id: "ml", label: "мл", toBase: 1e-6 },
      { id: "gal", label: "галлон (US)", toBase: 0.003785411784 },
      { id: "cm3", label: "см³", toBase: 1e-6 },
    ],
  },
];

function tempToKelvin(value: number, unit: string): number {
  if (unit === "c") return value + 273.15;
  if (unit === "f") return ((value - 32) * 5) / 9 + 273.15;
  if (unit === "k") return value;
  return NaN;
}

function tempFromKelvin(kelvin: number, unit: string): number {
  if (unit === "c") return kelvin - 273.15;
  if (unit === "f") return ((kelvin - 273.15) * 9) / 5 + 32;
  if (unit === "k") return kelvin;
  return NaN;
}

export function getCategory(id: string): UnitCategory | undefined {
  return UNIT_CATEGORIES.find((c) => c.id === id);
}

export function convertUnits(
  value: number,
  categoryId: string,
  fromId: string,
  toId: string
): number {
  if (!Number.isFinite(value)) return NaN;
  const cat = getCategory(categoryId);
  if (!cat) return NaN;
  if (cat.kind === "temperature") {
    const k = tempToKelvin(value, fromId);
    return tempFromKelvin(k, toId);
  }
  const from = cat.units.find((u) => u.id === fromId);
  const to = cat.units.find((u) => u.id === toId);
  if (!from || !to || from.toBase === 0) return NaN;
  const base = value * from.toBase;
  return base / to.toBase;
}
